/**
 * RWBR — POST RACE INTERVIEW (`RWBR_postraceinterview.pdf`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  THIS CHART WAS RECORDED AS "15 OF 36 ROWS UNRECOVERABLE". THAT WAS WRONG.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every quote and every action on this sheet survives verbatim in
 * `claude/RAW_addon_charts.md` §5. What the PDF text layer destroyed was not the
 * content but the *pairing*: rows 44-66 were emitted as bare numbers with empty
 * cells, and their quotes and actions came out later as two separate,
 * out-of-order blocks. The sheet was never unreadable. It was unsorted.
 *
 * ── WHAT IS TRANSCRIBED, AND WHAT IS RECONSTRUCTED ─────────────────────────
 *
 * Three separate claims, with three different strengths of evidence. They are
 * tracked separately per row in `field_sources` (the D-021 pattern) so nobody
 * downstream has to remember which is which.
 *
 *   1. THE BAND STRUCTURE — six bands of six rows. **Settled, three ways.**
 *
 *      (a) The sheet's own category-definition block prints the six categories
 *          in this order: FUME! · TOP or MIDDLE · Wrecked! · DNF! · Winner! ·
 *          Feud!  That is the printed order.
 *      (b) Rows 11-43 are transcribed and occupy exactly the first three and a
 *          half bands of that order, six rows apiece: 11-16 FUME, 21-26 TOP or
 *          MIDDLE, 31-36 WRECKED, 41-43 DNF. Extending the same 6/6/6 stride
 *          gives 41-46 DNF, 51-56 WINNER, 61-66 FEUD, which consumes all 36
 *          rows exactly.
 *      (c) It closes on the counts. Fifteen rows are missing; fifteen quotes are
 *          orphaned. Sorted by flavour they are 6 celebration, 6 conflict and 3
 *          mechanical disappointment — against 6 WINNER, 6 FEUD and the 3 DNF
 *          slots left over after 41-43. A wrong band assignment leaves a
 *          remainder. This one does not.
 *
 *      Corroborated a fourth time by the actions: "Feud Ends- remove racers from
 *      list", "Place BOTH on Fume List", "First Duel - Roll on Feud table" and
 *      "Pushing, Shoving, Punches, -Suspensions?" cannot belong to any band but
 *      FEUD, which is where (b) independently puts rows 61-66.
 *
 *      Recorded as `transcribed` for 11-43 and `derived` for 44-66 — derived,
 *      not reconstructed, because it follows from printed evidence rather than
 *      from a choice.
 *
 *   2. THE BAND EACH ORPHANED QUOTE BELONGS TO — **derived.** Flavour plus the
 *      count closure above. "I got the win, but everyone on this team deserves
 *      the credit!" is not a DNF line and there is no band but WINNER for it to
 *      sit in.
 *
 *   3. THE ORDER WITHIN A BAND, AND WHICH ACTION PAIRS WITH WHICH QUOTE —
 *      **reconstructed, and genuinely undetermined.** The text layer preserves
 *      no row order inside the tail blocks. Encoded as the source's own
 *      extraction order, which is the only non-arbitrary ordering available, and
 *      marked `reconstructed` on every affected field.
 *
 *      For the actions there is one binding constraint on top of extraction
 *      order: the four FEUD-only actions must land in the FEUD band. They are
 *      placed in the last four FEUD rows in extraction order, and the remaining
 *      eleven actions fill the remaining eleven slots in row order. That rule is
 *      stated here in full so a clean re-scan can overwrite it in one edit —
 *      see `INTERVIEW_AUDIT` and `applyCleanScan`.
 *
 * NOTHING IS BLANK. Nothing is invented either: every string below appears
 * verbatim in the source, including its typos ("feed up", "get's", "amazin'").
 *
 * ── HOW IT IS ROLLED ───────────────────────────────────────────────────────
 *
 * Printed procedure, verbatim: *"At end of race, roll two dice. Choose random
 * driver in that category and apply ACTION TAKEN result for next race. If
 * category has no applicable racer(s), ignore roll and continue to next race."*
 *
 * "Roll two dice" here is an 11-66 TWO-DIGIT READ, not a 2-12 sum — the index
 * runs 11 to 66 with no 2..10 rows. This is the trap `dice.ts` warns about:
 * `RWBR_trouble_option.pdf` says "roll two dice" on the same page of the same
 * game and means a sum. Use `code1166()`, never `sum2d6()`.
 */

import type { Dice } from './dice.ts';

export const CITE_INTERVIEW =
  'RWBR_postraceinterview.pdf — "RED WHITE AND BLUE RACING - POST RACE INTERVIEW", transcribed in ' +
  'claude/RAW_addon_charts.md §5. No author, league or date is printed on the sheet; do not attribute.';

export const CITE_PROCEDURE =
  'RWBR_postraceinterview.pdf — "At end of race, roll two dice. Choose random driver in that ' +
  'category and apply ACTION TAKEN result for next race. If category has no applicable racer(s), ' +
  'ignore roll and continue to next race."';

export const CITE_BANDS =
  'Band structure derived three ways: the sheet\'s own printed category order; the 6/6/6 stride ' +
  'confirmed by transcribed rows 11-43; and the 6/6/3 flavour split of the 15 orphaned quotes ' +
  'closing exactly against 6 WINNER + 6 FEUD + 3 DNF slots.';

export const CITE_WITHIN_BAND =
  'Order within bands 44-66 and the quote-to-action pairing are NOT recoverable from the PDF text ' +
  'layer. Encoded as the source\'s own extraction order. See INTERVIEW_AUDIT.';

// ---------------------------------------------------------------------------
// Bands
// ---------------------------------------------------------------------------

export type InterviewBand = 'FUME' | 'TOP_OR_MIDDLE' | 'WRECKED' | 'DNF' | 'WINNER' | 'FEUD';

/** The category definitions, verbatim from the sheet. */
export const BAND_DEFINITION: Readonly<Record<InterviewBand, string>> = Object.freeze({
  FUME: 'Refers to racers that were placed on list THIS race.',
  TOP_OR_MIDDLE: 'Randomly choose ONE racer.',
  WRECKED: 'A racer who wrecked out of race (Trouble or Problem).',
  DNF: 'Refers to Non-Wrecked racers only.',
  WINNER: 'Winner of race.',
  FEUD: 'One random racer that will be placed on list for NEXT race.',
});

/** Printed order of the six categories, which is also the row order. */
export const BAND_ORDER: readonly InterviewBand[] = [
  'FUME',
  'TOP_OR_MIDDLE',
  'WRECKED',
  'DNF',
  'WINNER',
  'FEUD',
];

/** 11-16 → FUME, 21-26 → TOP_OR_MIDDLE, … 61-66 → FEUD. */
export function bandForCode(code: string): InterviewBand {
  const tens = Number(code[0]);
  if (!Number.isInteger(tens) || tens < 1 || tens > 6) {
    throw new RangeError(`post-race interview: no band for code ${code}`);
  }
  return BAND_ORDER[tens - 1];
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

/**
 * The ACTION TAKEN column, read into a closed set. The sheet's own
 * "ACTION TAKEN - CLARIFICATIONS" block defines each one; the clarification is
 * carried on the entry so the season layer never has to re-read the PDF.
 */
export type InterviewActionKind =
  | 'ignore-out-once'
  | 'gain-double-fast-pit'
  | 'one-successful-fast-pit'
  | 'gain-brake'
  | 'gain-gas-pedal'
  | 'lose-gas-pedal'
  | 'lose-a-quality'
  | 'gain-new-quality'
  | 'gain-heat'
  | 'star-a-quality'
  | 'increase-escape'
  | 'increase-repair'
  | 'increase-fast-pit'
  | 'auto-win-quality-challenge'
  | 'auto-escape-once'
  | 'gain-performance-chip'
  | 'tv-list'
  | 'fume-list'
  | 'first-duel-feud-fume'
  | 'feud-ends'
  | 'place-both-on-fume-list'
  | 'pushing-shoving-punches';

export interface InterviewAction {
  kind: InterviewActionKind;
  /** The ACTION TAKEN cell, verbatim. */
  text: string;
  /** Magnitude where the sheet prints one ("Increase Escape by 2" → 2). */
  amount?: number;
}

/**
 * ACTION TAKEN - CLARIFICATIONS, verbatim from the sheet.
 *
 * Two ceilings are printed here and nowhere else in the RWBR material:
 * "Cannot exceed 5 Gas Pedals or 3 Brakes", and the floors "cannot have less
 * than one Gas Pedal or less than one quality". EZRA caps Fast Pit at 9
 * (EZRA-S2 §3.9); this sheet states no ceiling for Escape, Repair or Fast Pit.
 */
export const ACTION_CLARIFICATION: Readonly<Record<InterviewActionKind, string>> = Object.freeze({
  'ignore-out-once': 'IGNORE OUT ONCE- Ignore any OUT result (one time) from the Problem or Feud/Fume chart.',
  'gain-double-fast-pit':
    'GAIN ONE DBL FAST PIT- On first ELIGIBLE pit roll for Fast Pit, Racer automatically succeeds fast pit and double fast pit dice rolls . Racer is given 2 Performance chips.',
  'one-successful-fast-pit':
    'ONE SUCCESSFUL FAST PIT- On first ELIGIBLE pit roll for Fast Pit, Racer automatically succeeds fast pit dice roll.',
  'gain-brake':
    'GAIN ONE BRAKE or GAS PEDAL- For the first third of the race, Racer is given one extra Brake or Gas Pedal. Cannot exceed 5 Gas Pedals or 3 Brakes.',
  'gain-gas-pedal':
    'GAIN ONE BRAKE or GAS PEDAL- For the first third of the race, Racer is given one extra Brake or Gas Pedal. Cannot exceed 5 Gas Pedals or 3 Brakes.',
  'lose-gas-pedal':
    'LOSE ONE GAS PEDAL or QUALITY- Racer loses one Gas Pedal or a Random Quality. Racer cannot have less than one Gas Pedal or less than one quality.',
  'lose-a-quality':
    'LOSE ONE GAS PEDAL or QUALITY- Racer loses one Gas Pedal or a Random Quality. Racer cannot have less than one Gas Pedal or less than one quality.',
  'gain-new-quality': 'GAIN NEW QUALITY - Racer gains one quality he does not already have.',
  'gain-heat': 'GAIN HEAT - Racer gains HEAT quality. If Racer already has HEAT, quality is now Starred.',
  'star-a-quality': 'STAR A QUALITY - One existing racer quality (not starred) is given a star.',
  'increase-escape': "INCREASE ESCAPE/ REPAIR/ FAST PIT - Racer's escape, repair, or fast pit # is increased by 1 or 2.",
  'increase-repair': "INCREASE ESCAPE/ REPAIR/ FAST PIT - Racer's escape, repair, or fast pit # is increased by 1 or 2.",
  'increase-fast-pit': "INCREASE ESCAPE/ REPAIR/ FAST PIT - Racer's escape, repair, or fast pit # is increased by 1 or 2.",
  'auto-win-quality-challenge':
    'AUTO WIN ONE QUALITY CHALLENGE- Racer automatically wins Quality Challenge. Ignore performance chips.',
  'auto-escape-once': 'AUTO ESCAPE ONCE- Racer automatically succeeds first escape roll .',
  'gain-performance-chip': 'Racer is given one Performance chip. (No separate clarification is printed.)',
  'tv-list': 'TV / FUME List- Driver is immediately placed on list for next race.',
  'fume-list': 'TV / FUME List- Driver is immediately placed on list for next race.',
  'first-duel-feud-fume':
    'FIRST DUEL- ROLL ON FEUD/FUME CHART- One first Duel roll, immediately roll on Feud/Fume Chart for Racers involved.',
  'feud-ends': 'Feud Ends- remove racers from list. (No separate clarification is printed.)',
  'place-both-on-fume-list':
    'PLACE BOTH ON FUME LIST- Each of the two feuding racers are removed from the Feud List and placed separately on the Fume List for the next race potentially starting a new feud with other racers.',
  'pushing-shoving-punches':
    'PUSHING, SHOVING, PUNCHES- SUSPENSIONS?- Words and finger pointing turn into action and fisticuffs! Race Officials (YOU) have to decide if any action needs to be taken before next race- Suspensions? Penalty points? Fines?',
});

/**
 * The one action on the sheet with no mechanical resolution at all. The sheet
 * hands it to the human: *"Race Officials (YOU) have to decide."* It is not
 * automated and it is not silently dropped — callers get `needsHumanRuling`.
 */
export const ACTION_NEEDS_HUMAN: readonly InterviewActionKind[] = ['pushing-shoving-punches'];

/** Printed ceilings and floors, from the clarifications block. */
export const GAS_PEDAL_MAX = 5;
export const BRAKE_MAX = 3;
export const GAS_PEDAL_MIN = 1;
export const QUALITIES_MIN = 1;
export const CITE_CAPS =
  'RWBR_postraceinterview.pdf clarifications — "Cannot exceed 5 Gas Pedals or 3 Brakes" and "Racer ' +
  'cannot have less than one Gas Pedal or less than one quality". No ceiling is printed for Escape, ' +
  'Repair or Fast Pit on this sheet; EZRA-S2 §3.9 separately caps Fast Pit at 9.';

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * `transcribed` — read verbatim off the source, cell and row together.
 * `derived`     — the cell is verbatim; its placement follows from printed
 *                 evidence (band order, stride, count closure) rather than a
 *                 choice.
 * `reconstructed` — the cell is verbatim; its placement is our ordering rule,
 *                 because the source preserves no order here.
 */
export type FieldSource = 'transcribed' | 'derived' | 'reconstructed';

export interface InterviewRow {
  /** 11-66. */
  code: string;
  band: InterviewBand;
  /** POST RACE - INTERVIEW NOTES, verbatim including typos. */
  quote: string;
  action: InterviewAction;
  field_sources: {
    band: FieldSource;
    quote: FieldSource;
    action: FieldSource;
  };
}

const T = { band: 'transcribed', quote: 'transcribed', action: 'transcribed' } as const;
/** Band derived from printed evidence; quote band derived; placement reconstructed. */
const R = { band: 'derived', quote: 'reconstructed', action: 'reconstructed' } as const;

// ---------------------------------------------------------------------------
// The chart
// ---------------------------------------------------------------------------

export const POST_RACE_INTERVIEW: readonly InterviewRow[] = [
  // ─── 11-16 FUME! ─────────────────────────────────────────── transcribed ──
  { code: '11', band: 'FUME', quote: "That really caught me by surprise today. I'll be better prepared next week.", action: { kind: 'ignore-out-once', text: 'Ignore OUT once' }, field_sources: T },
  { code: '12', band: 'FUME', quote: 'Everything that happened was bad. We got some things to work on for sure.', action: { kind: 'gain-double-fast-pit', text: 'Gain One DBL Fast Pit' }, field_sources: T },
  { code: '13', band: 'FUME', quote: "I'm just out there to race, but now I'm feed up with @#$% like that!", action: { kind: 'gain-heat', text: 'Gain HEAT' }, field_sources: T },
  { code: '14', band: 'FUME', quote: 'Nobody is going to put me in a position like that again! You can bet on that!', action: { kind: 'gain-brake', text: 'Gain One Brake', amount: 1 }, field_sources: T },
  { code: '15', band: 'FUME', quote: 'The crew and I will be ready next time, period.', action: { kind: 'one-successful-fast-pit', text: 'One Successful Fast Pit' }, field_sources: T },
  { code: '16', band: 'FUME', quote: "That crap really held us back today, so I'm going to settle this next week!", action: { kind: 'first-duel-feud-fume', text: 'First Duel - Roll on Feud/Fume Chart' }, field_sources: T },

  // ─── 21-26 TOP or MIDDLE ─────────────────────────────────── transcribed ──
  { code: '21', band: 'TOP_OR_MIDDLE', quote: "Had too many guys get pass me today. I've got to do a better job holding'em off.", action: { kind: 'gain-brake', text: 'Gain One Brake', amount: 1 }, field_sources: T },
  { code: '22', band: 'TOP_OR_MIDDLE', quote: "We got close this time no doubt about it! I'm already ready for the next one!", action: { kind: 'gain-performance-chip', text: 'Gain Performance Chip' }, field_sources: T },
  { code: '23', band: 'TOP_OR_MIDDLE', quote: "Wow! That was pretty exciting! We'll get it done next time", action: { kind: 'tv-list', text: 'TV List' }, field_sources: T },
  { code: '24', band: 'TOP_OR_MIDDLE', quote: 'Everybody worked so hard and to come up short like that…I just hate it!', action: { kind: 'fume-list', text: 'FUME List' }, field_sources: T },
  { code: '25', band: 'TOP_OR_MIDDLE', quote: "I'm happy with where we are, I'm not worried at all.", action: { kind: 'lose-gas-pedal', text: 'Lose One Gas Pedal', amount: 1 }, field_sources: T },
  { code: '26', band: 'TOP_OR_MIDDLE', quote: 'Well I feel good about this one. I know I can get more out of this car next time.', action: { kind: 'gain-new-quality', text: 'Gain NEW Quality' }, field_sources: T },

  // ─── 31-36 WRECKED! ──────────────────────────────────────── transcribed ──
  { code: '31', band: 'WRECKED', quote: "That one's on me. I just didn't see him in time and got tangled up.", action: { kind: 'increase-escape', text: 'Increase Escape by 2', amount: 2 }, field_sources: T },
  { code: '32', band: 'WRECKED', quote: 'The crew was working pretty hard to get me back out there. Just ran out of time.', action: { kind: 'increase-repair', text: 'Increase Repair by 1', amount: 1 }, field_sources: T },
  { code: '33', band: 'WRECKED', quote: "I feel like the car was running really good. Guess we'll get them next time.", action: { kind: 'gain-gas-pedal', text: 'Gain One Gas Pedal', amount: 1 }, field_sources: T },
  { code: '34', band: 'WRECKED', quote: "Whew! That was a lot of clang'in and bang'in, and some scrub'in and rub'in!", action: { kind: 'tv-list', text: 'TV List' }, field_sources: T },
  { code: '35', band: 'WRECKED', quote: 'Well this stuff happens. We will make some adjustments and get back out there!', action: { kind: 'auto-win-quality-challenge', text: 'Auto Win One Quality Challenge' }, field_sources: T },
  { code: '36', band: 'WRECKED', quote: "The car was perfect! I don't know if we are going to be able to do that again!", action: { kind: 'lose-a-quality', text: 'Lose A Quality' }, field_sources: T },

  // ─── 41-43 DNF! ──────────────────────────────────────────── transcribed ──
  { code: '41', band: 'DNF', quote: 'I know the guys will work hard to keep that from happening again.', action: { kind: 'increase-repair', text: 'Increase Repair by 2', amount: 2 }, field_sources: T },
  { code: '42', band: 'DNF', quote: 'The car felt pretty good, looks like we just need to make a few adjustments.', action: { kind: 'gain-performance-chip', text: 'Gain Performance Chip' }, field_sources: T },
  { code: '43', band: 'DNF', quote: "It'll be a long night 'cause I know the crew wants to find out what went wrong.", action: { kind: 'increase-fast-pit', text: 'Increase Fast Pit by 1', amount: 1 }, field_sources: T },

  // ─── 44-46 DNF! ───────────────────────────────────────── reconstructed ──
  // Quotes: tail-block A entries 3, 4 and 15 — the three mechanical-
  // disappointment lines, in extraction order.
  { code: '44', band: 'DNF', quote: "Heck ya I'm disappointed! We just put in too much work to have that happen!", action: { kind: 'gain-new-quality', text: 'Gain NEW Quality' }, field_sources: R },
  { code: '45', band: 'DNF', quote: "It has to be the worse luck ever! I'm just really upset right now!", action: { kind: 'auto-escape-once', text: 'Auto Escape Once' }, field_sources: R },
  { code: '46', band: 'DNF', quote: "When I know the crew works that hard, I can't let them down.", action: { kind: 'lose-a-quality', text: 'Lose A Quality' }, field_sources: R },

  // ─── 51-56 WINNER! ────────────────────────────────────── reconstructed ──
  // Quotes: tail-block A entries 5, 6, 7, 8, 13 and 14 — the six celebration
  // lines, in extraction order.
  { code: '51', band: 'WINNER', quote: "Wow, that was really close! We almost didn't pull that off!", action: { kind: 'increase-escape', text: 'Increase Escape by 1', amount: 1 }, field_sources: R },
  { code: '52', band: 'WINNER', quote: 'I got the win, but everyone on this team deserves the credit!', action: { kind: 'gain-gas-pedal', text: 'Gain One Gas Pedal', amount: 1 }, field_sources: R },
  { code: '53', band: 'WINNER', quote: "Honestly, I'm surprised! Everything seemed to go our way today.", action: { kind: 'star-a-quality', text: 'Star one Quality' }, field_sources: R },
  { code: '54', band: 'WINNER', quote: 'I guess I was just in the right place at the right time!', action: { kind: 'fume-list', text: 'FUME List' }, field_sources: R },
  { code: '55', band: 'WINNER', quote: "The crew did some amazing work in the pits today I'll tell ya that!", action: { kind: 'lose-gas-pedal', text: 'Lose One Gas Pedal', amount: 1 }, field_sources: R },
  { code: '56', band: 'WINNER', quote: "That was pretty amazin' racin' right there folks!", action: { kind: 'gain-performance-chip', text: 'Gain Performance Chip' }, field_sources: R },

  // ─── 61-66 FEUD! ──────────────────────────────────────── reconstructed ──
  // Quotes: tail-block A entries 1, 2, 9, 10, 11 and 12 — the six conflict
  // lines, in extraction order.
  // Actions: the two remaining unconstrained actions in extraction order, then
  // the four FEUD-only actions in extraction order. See INTERVIEW_AUDIT.
  { code: '61', band: 'FEUD', quote: "It was a complete misunderstanding and now it's over.", action: { kind: 'gain-brake', text: 'Gain One Brake', amount: 1 }, field_sources: R },
  { code: '62', band: 'FEUD', quote: "That's what is going to happen when somebody gets in my face!", action: { kind: 'one-successful-fast-pit', text: 'One Successful Fast Pit' }, field_sources: R },
  { code: '63', band: 'FEUD', quote: "I'm going to ignore the non-sense out there and just race.", action: { kind: 'feud-ends', text: 'Feud Ends- remove racers from list' }, field_sources: R },
  { code: '64', band: 'FEUD', quote: "I'll see that he get's what's coming to him next week!", action: { kind: 'pushing-shoving-punches', text: 'Pushing, Shoving, Punches, -Suspensions?' }, field_sources: R },
  { code: '65', band: 'FEUD', quote: 'When guys race like that it ruins the entire sport you know what I mean!', action: { kind: 'first-duel-feud-fume', text: 'First Duel - Roll on Feud table' }, field_sources: R },
  { code: '66', band: 'FEUD', quote: "I've seen it before. Best thing to do is just concentrate on the next race.", action: { kind: 'place-both-on-fume-list', text: 'Place BOTH on Fume List' }, field_sources: R },
];

export const INTERVIEW_BY_CODE: Readonly<Record<string, InterviewRow>> = Object.freeze(
  Object.fromEntries(POST_RACE_INTERVIEW.map((r) => [r.code, r])),
);

// ---------------------------------------------------------------------------
// The audit — so a clean re-scan replaces the guess in one edit
// ---------------------------------------------------------------------------

/**
 * The four actions that can only belong to the FEUD band. This is the one hard
 * constraint the flavour analysis produced, and it is what stops rows 44-66
 * from being filled by raw extraction order alone.
 */
export const FEUD_ONLY_ACTIONS: readonly InterviewActionKind[] = [
  'feud-ends',
  'pushing-shoving-punches',
  'first-duel-feud-fume',
  'place-both-on-fume-list',
];

export interface InterviewAudit {
  totalRows: number;
  transcribedRows: number;
  reconstructedRows: number;
  /** Codes whose within-band placement is our ordering rule, not the source. */
  reconstructedCodes: readonly string[];
  /** Exactly what the ordering rule was, in one sentence. */
  orderingRule: string;
  /** What a clean re-scan has to supply to remove the reconstruction entirely. */
  toReplace: string;
}

export const INTERVIEW_AUDIT: InterviewAudit = Object.freeze({
  totalRows: 36,
  transcribedRows: 21,
  reconstructedRows: 15,
  reconstructedCodes: Object.freeze([
    '44', '45', '46',
    '51', '52', '53', '54', '55', '56',
    '61', '62', '63', '64', '65', '66',
  ]),
  orderingRule:
    'Quotes: tail-block A filtered to the row\'s band, in raw extraction order. ' +
    'Actions: the four FEUD-only actions occupy rows 63-66 in extraction order; the remaining ' +
    'eleven actions fill 44, 45, 46, 51-56, 61, 62 in row order, taken in extraction order.',
  toReplace:
    'A clean re-scan or re-export of RWBR_postraceinterview.pdf that preserves cell adjacency for ' +
    'rows 44-66. Replace the quote and action on those fifteen rows and set field_sources to ' +
    "'transcribed'. Nothing else in this module changes: the bands are already derived from " +
    'printed evidence and will not move.',
});

/**
 * Sanity checks that must hold for the reconstruction to be self-consistent.
 * The tests call this; so can a caller that wants to fail loudly at boot.
 */
export function verifyInterviewChart(): string[] {
  const problems: string[] = [];

  if (POST_RACE_INTERVIEW.length !== 36) {
    problems.push(`expected 36 rows, found ${POST_RACE_INTERVIEW.length}`);
  }

  // Every 11-66 code exactly once.
  for (let tens = 1; tens <= 6; tens++) {
    for (let ones = 1; ones <= 6; ones++) {
      const code = `${tens}${ones}`;
      if (!INTERVIEW_BY_CODE[code]) problems.push(`missing row ${code}`);
    }
  }

  // Six rows per band, in printed order.
  for (const band of BAND_ORDER) {
    const n = POST_RACE_INTERVIEW.filter((r) => r.band === band).length;
    if (n !== 6) problems.push(`band ${band} has ${n} rows, expected 6`);
  }
  for (const row of POST_RACE_INTERVIEW) {
    if (row.band !== bandForCode(row.code)) {
      problems.push(`row ${row.code} is banded ${row.band} but its code says ${bandForCode(row.code)}`);
    }
  }

  // No blanks, ever. This is the whole point.
  for (const row of POST_RACE_INTERVIEW) {
    if (!row.quote.trim()) problems.push(`row ${row.code} has an empty quote`);
    if (!row.action.text.trim()) problems.push(`row ${row.code} has an empty action`);
  }

  // Every quote is distinct — the source prints 36 different lines.
  const quotes = new Set(POST_RACE_INTERVIEW.map((r) => r.quote));
  if (quotes.size !== 36) problems.push(`expected 36 distinct quotes, found ${quotes.size}`);

  // The FEUD-only actions really are in the FEUD band.
  for (const kind of FEUD_ONLY_ACTIONS) {
    for (const row of POST_RACE_INTERVIEW.filter((r) => r.action.kind === kind)) {
      if (row.band !== 'FEUD' && row.code !== '16') {
        problems.push(`FEUD-only action ${kind} appears at ${row.code} in band ${row.band}`);
      }
    }
  }

  // The audit's own numbers must match the table.
  const recon = POST_RACE_INTERVIEW.filter((r) => r.field_sources.quote === 'reconstructed');
  if (recon.length !== INTERVIEW_AUDIT.reconstructedRows) {
    problems.push(
      `audit claims ${INTERVIEW_AUDIT.reconstructedRows} reconstructed rows, table has ${recon.length}`,
    );
  }
  const auditCodes = [...INTERVIEW_AUDIT.reconstructedCodes].sort().join(',');
  const tableCodes = recon.map((r) => r.code).sort().join(',');
  if (auditCodes !== tableCodes) {
    problems.push(`audit reconstructed codes ${auditCodes} != table ${tableCodes}`);
  }

  return problems;
}

/**
 * Replace the reconstructed rows with a clean scan, in one call.
 *
 * This exists so the reconstruction is never load-bearing. Hand it the fifteen
 * rows off a re-export and everything downstream keeps working, with
 * `field_sources` flipped to `transcribed`.
 */
export function applyCleanScan(
  scanned: readonly { code: string; quote: string; action: InterviewAction }[],
): InterviewRow[] {
  const byCode = new Map(scanned.map((s) => [s.code, s]));
  return POST_RACE_INTERVIEW.map((row) => {
    const s = byCode.get(row.code);
    if (!s) return row;
    return {
      ...row,
      quote: s.quote,
      action: s.action,
      field_sources: { band: row.field_sources.band, quote: 'transcribed', action: 'transcribed' },
    };
  });
}

// ---------------------------------------------------------------------------
// Eligibility
// ---------------------------------------------------------------------------

/**
 * Who is available in each category when the roll is made.
 *
 * Supplied by the caller rather than computed here, because only the season
 * layer knows the FUME list, the FEUD list and the TV list — this module owns
 * the chart, not the bookkeeping.
 */
export interface InterviewEligibility {
  /** "Racers that were placed on list THIS race." */
  fume: readonly string[];
  /** Any racer finishing in the TOP or MIDDLE group. */
  topOrMiddle: readonly string[];
  /** "A racer who wrecked out of race (Trouble or Problem)." */
  wrecked: readonly string[];
  /** "Non-Wrecked racers only" who did not finish. */
  dnf: readonly string[];
  /** "Winner of race." At most one. */
  winner: readonly string[];
  /** Racers eligible to be put on the FEUD list for next race. */
  feud: readonly string[];
}

export function candidatesFor(
  band: InterviewBand,
  eligibility: InterviewEligibility,
): readonly string[] {
  switch (band) {
    case 'FUME': return eligibility.fume;
    case 'TOP_OR_MIDDLE': return eligibility.topOrMiddle;
    case 'WRECKED': return eligibility.wrecked;
    case 'DNF': return eligibility.dnf;
    case 'WINNER': return eligibility.winner;
    case 'FEUD': return eligibility.feud;
  }
}

// ---------------------------------------------------------------------------
// The roll
// ---------------------------------------------------------------------------

export interface InterviewResult {
  code: string;
  band: InterviewBand;
  row: InterviewRow;
  /** The driver the action lands on, or null when the category was empty. */
  driverId: string | null;
  /** True when the roll was ignored because nobody qualified. */
  ignored: boolean;
  /** The sheet hands this one to the human. */
  needsHumanRuling: boolean;
  /** True when any of this row's three fields is a reconstruction. */
  reconstructed: boolean;
  description: string;
  ruleCitation: string;
  dice: { red: number; white: number; code: string };
}

/**
 * Roll the post-race interview. ONE roll per race, at the end of the race.
 *
 * The category is chosen by the dice, not by the field: if the roll lands in a
 * band with nobody in it, the printed rule is to *ignore the roll* — not to
 * re-roll, and not to pick a different band. That is what makes the effective
 * rate of this chart far lower than 1.0 per race, and it is the number worth
 * measuring.
 */
export function rollPostRaceInterview(
  dice: Dice,
  eligibility: InterviewEligibility,
): InterviewResult {
  const roll = dice.code1166();
  const row = INTERVIEW_BY_CODE[roll.code];
  if (!row) throw new RangeError(`post-race interview: no row for code ${roll.code}`);

  const band = row.band;
  const pool = candidatesFor(band, eligibility);
  const reconstructed =
    row.field_sources.quote === 'reconstructed' || row.field_sources.action === 'reconstructed';

  if (pool.length === 0) {
    return {
      code: roll.code,
      band,
      row,
      driverId: null,
      ignored: true,
      needsHumanRuling: false,
      reconstructed,
      ruleCitation: CITE_PROCEDURE,
      dice: { red: roll.red, white: roll.white, code: roll.code },
      description:
        `${roll.code} — ${band.replace(/_/g, ' ')}. Nobody qualifies in that category tonight, ` +
        `so the roll is ignored and we go to next race.`,
    };
  }

  // "Choose random driver in that category."
  const driverId = pool[dice.d(pool.length) - 1];
  const needsHumanRuling = ACTION_NEEDS_HUMAN.includes(row.action.kind);

  return {
    code: roll.code,
    band,
    row,
    driverId,
    ignored: false,
    needsHumanRuling,
    reconstructed,
    ruleCitation: reconstructed ? `${CITE_INTERVIEW}; ${CITE_WITHIN_BAND}` : CITE_INTERVIEW,
    dice: { red: roll.red, white: roll.white, code: roll.code },
    description:
      `${roll.code} — ${band.replace(/_/g, ' ')}. "${row.quote}" — ${driverId}. ` +
      `Next race: ${row.action.text}.` +
      (needsHumanRuling ? ' Race Officials have to decide what happens before the next race.' : ''),
  };
}
